import React, { useState, useEffect, useCallback, useRef } from "react";
import { Panel, Table, Loader, Icon, Dropdown } from "@culturehq/components";
import * as CHQ from "chq";

import analytics from "images/analytics.svg";

import { useGet } from "app/hooks";
import { ProfileLink, StoryLink } from "app/links";
import { formatThousand, t, Trans } from "app/utils";
import { DateFormat } from "components/common/Time";
import UserThumbnail from "components/person/UserThumbnail";
import { Params } from "@culturehq/client";

type StoryAnalyticsProps = {
  storyAnalytics: CHQ.StoryAnalytics;
};

const StoryAnalytics = React.forwardRef<HTMLTableRowElement, StoryAnalyticsProps>(
  ({ storyAnalytics }, ref) => (
    <tr ref={ref}>
      <td className="max-width-300 StoryAnalyticsInfo--Prompt">
        <StoryLink story={storyAnalytics.story} tabIndex={0}>
          <p className="StoryAnalyticsInfo--Prompt__Info">{storyAnalytics.story.question.question}</p>
        </StoryLink>
        <p className="StoryAnalyticsInfo--Prompt__Info-Date">
          <Trans>Published</Trans>
          {" "}
          <DateFormat time={storyAnalytics.story.createdAt} format="%m/%d/%y" />
        </p>
      </td>
      <td>
        <div className="StoryAnalyticsInfo--User">
          <ProfileLink user={storyAnalytics.story.creator}>
            <UserThumbnail user={storyAnalytics.story.creator} />
            <div>
              <p className="StoryAnalyticsInfo--User__Info">{storyAnalytics.story.creator.name}</p>
              <p className="StoryAnalyticsInfo--User__Info StoryAnalyticsInfo--User__Info-Title">{storyAnalytics.story.creator.title}</p>
            </div>
          </ProfileLink>
        </div>
      </td>
      <td>
        <div className="StoryAnalyticsInfo flx-row">
          <div className="StoryAnalyticsInfo--Data flx-row">
            <Icon icon="eye" />
            <span>{formatThousand(storyAnalytics.views)}</span>
          </div>
          <div className="StoryAnalyticsInfo--Data flx-row">
            <Icon icon="linkedin-thumbsup" />
            <span>{formatThousand(storyAnalytics.reactions)}</span>
          </div>
          <div className="StoryAnalyticsInfo--Data flx-row">
            <Icon icon="social-comment" />
            <span>{formatThousand(storyAnalytics.comments)}</span>
          </div>
        </div>
      </td>
    </tr>
  )
);

export type ListStoryAnalytics = {
  stories: CHQ.StoryAnalytics[];
  pagination: any;
};

interface HighestPerformingStoriesContentProps {
  params: Params;
  sortBy: string;
}

const HighestPerformingStoriesContent: React.FC<HighestPerformingStoriesContentProps> = ({
  params, sortBy
}) => {
  const [page, setPage] = useState(1);
  const [stories, setStories] = useState<CHQ.StoryAnalytics[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState(2);
  // Call useGet to get story data
  const { getting, got, error } = useGet<ListStoryAnalytics>(`/analytics/social/stories?sort_by=${sortBy}&page=${page}`, params);

  useEffect(() => {
    setPage(1);
    setStories([]);
  }, [params, sortBy]);

  useEffect(() => {
    // Check if data has been obtained
    if (got) {
      setTotalPages(got.pagination.totalPages);
      // fetched stories
      if (page === 1) {
        setStories(got.stories);
      } else {
        // Concatenate the new stories with the existing ones if it is not the first page
        setStories(prevStories => [...prevStories, ...got.stories]);
      }
      setHasMore(got.pagination.currentPage < got.pagination.totalPages);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [got]);

  const observer = useRef<IntersectionObserver | null>(null);

  // handle last visible item in stories list
  const lastStoryElementRef = useCallback(
    lastVisibleStory => {
      if (getting || !hasMore || !lastVisibleStory) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          setPage(prevPage => {
            if (totalPages) {
              return prevPage < totalPages ? prevPage + 1 : totalPages;
            }
            return prevPage + 1;
          });
        }
      });
      observer.current.observe(lastVisibleStory);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getting, hasMore]
  );

  if (getting && page === 1) {
    return <Loader loading />;
  }

  if (error) {
    throw new Error(t("Looks like there has been an unexpected error. Please reload the page."));
  }

  return (
    <>
      <Table className="table-responsive StoryAnalyticsTable">
        <thead>
          <tr>
            <th>
              <Trans>Story</Trans>
            </th>
            <th className="txt-center">
              <Trans>Author</Trans>
            </th>
            <th className="txt-center">
              <Trans>Engagement</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {stories.map((storyAnalytics, index) => (
            <StoryAnalytics
              key={storyAnalytics.story.id}
              storyAnalytics={storyAnalytics}
              // only the last visible element  references lastStoryElementRef
              ref={index === stories.length - 1 ? lastStoryElementRef : null}
            />
          ))}
        </tbody>
      </Table>
      {stories.length === 0 && (
        <div className="HighestStoryAnalyticsTable--Empty">
          <img className="HighestStoryAnalyticsTable--Empty__Image" src={analytics} alt={t("Empty data")} />
          <h4 className="HighestStoryAnalyticsTable--Empty__Label"><Trans>No data to display yet</Trans></h4>
        </div>
      )}
      {getting && <Loader loading />}
    </>
  );
};

interface SocialHighestPerformingStoriesProps {
  params: Params;
}

const SocialHighestPerformingStories: React.FC<SocialHighestPerformingStoriesProps> = ({
  params
}) => {
  const [sortBy, setSortBy] = useState("engagement");

  return (
    <Panel>
      <Panel.Body className="card_holder">
        <div className="card_title">
          <h3>
            <Trans>Highest-Performing Stories</Trans>
          </h3>
          <Dropdown selected={sortBy} onChange={setSortBy}>
            <Dropdown.Button>
              Sort By
            </Dropdown.Button>
            <Dropdown.ListBox className="custom_option">
              <Dropdown.Option value="engagement">
                <Trans>Engagement</Trans>
              </Dropdown.Option>
              <Dropdown.Option value="views">
                <Trans>Views</Trans>
              </Dropdown.Option>
              <Dropdown.Option value="comments">
                <Trans>Comments</Trans>
              </Dropdown.Option>
              <Dropdown.Option value="reactions">
                <Trans>Reactions</Trans>
              </Dropdown.Option>
            </Dropdown.ListBox>
          </Dropdown>
        </div>
        <div className="card_info">
          <div className="w-100">
            <HighestPerformingStoriesContent params={params} sortBy={sortBy} />
          </div>
        </div>
      </Panel.Body>
    </Panel>
  );
};

export default SocialHighestPerformingStories;
